import { useState } from 'react';
import { Heart, AlertTriangle, MessageCircle, Sparkles } from 'lucide-react';
import { SIGNS } from '../../data/signs';
import { getCompatibility, type CompatibilityData } from '../../data/compatibility';

// ─────────────────────────────────────────
// SynastryWidget, interactive free compatibility analysis by sun signs.
// Shown on /servicos/sinastria to hook visitors before the paid service.
// ─────────────────────────────────────────

export function SynastryWidget() {
  const [sign1, setSign1] = useState<string>('');
  const [sign2, setSign2] = useState<string>('');
  const [result, setResult] = useState<CompatibilityData | null>(null);
  const [error, setError]   = useState<string>('');

  function handleAnalyze() {
    if (!sign1 || !sign2) {
      setError('Escolha os dois signos para continuar');
      return;
    }
    if (sign1 === sign2) {
      setError('Escolha dois signos diferentes para ver a compatibilidade');
      return;
    }
    setError('');
    const data = getCompatibility(sign1, sign2);
    setResult(data);
  }

  return (
    <section style={{
      padding: '3rem 1.5rem 2rem',
      maxWidth: '880px',
      margin: '0 auto',
    }}>
      <div className="card" style={{
        padding: 'clamp(1.5rem, 4vw, 2.25rem)',
        background: 'linear-gradient(180deg, rgba(245,200,66,0.06), rgba(0,0,0,0))',
        border: '1px solid var(--border-gold)',
        borderRadius: 'var(--radius-card)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>💫</div>
          <h2 className="font-display" style={{
            fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
            color: 'var(--text-primary)',
            marginBottom: '0.5rem',
          }}>
            Veja a compatibilidade gratuita entre dois signos
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body)', lineHeight: 1.6 }}>
            Escolha dois signos abaixo e descubra o panorama astral dessa combinação. É uma amostra, a Sinastria completa vai muito além.
          </p>
        </div>

        {/* Sign selectors */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}>
          <SignSelector label="Signo 1" value={sign1} onChange={setSign1} />
          <SignSelector label="Signo 2" value={sign2} onChange={setSign2} />
        </div>

        {error && (
          <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', textAlign: 'center', marginBottom: '0.75rem' }}>
            {error}
          </p>
        )}

        <button
          onClick={handleAnalyze}
          className="btn btn-primary btn-lg"
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
        >
          <Sparkles size={18} /> Ver compatibilidade
        </button>

        {/* Result */}
        {result && <CompatibilityResult data={result} />}
      </div>
    </section>
  );
}

// ─── Sign dropdown ────────────────────────────────────────────────
function SignSelector({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
      <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {label}
      </span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="form-input"
        style={{
          padding: '0.75rem 0.9rem',
          fontSize: '1rem',
          background: 'var(--bg-elevated)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
        }}
      >
        <option value="">Selecione...</option>
        {SIGNS.map(s => (
          <option key={s.slug} value={s.slug}>
            {s.emoji} {s.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// ─── Result card ──────────────────────────────────────────────────
function CompatibilityResult({ data }: { data: CompatibilityData }) {
  const scoreColor =
    data.score >= 80 ? '#4ade80' :
    data.score >= 65 ? '#c084fc' :
    data.score >= 50 ? '#f59e0b' :
                       '#f87171';

  return (
    <div className="animate-fade-in" style={{
      marginTop: '1.5rem',
      padding: '1.5rem',
      background: 'rgba(0,0,0,0.25)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border-subtle)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ fontSize: '1.15rem', color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>
          {data.title}
        </h3>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.3rem 0.75rem',
          borderRadius: '999px',
          border: `1px solid ${scoreColor}`,
          background: `${scoreColor}15`,
          fontSize: 'var(--text-caption)',
          fontWeight: 700,
          color: scoreColor,
        }}>
          <Sparkles size={14} /> {data.score}% afinidade
        </div>
      </div>

      <p style={{ color: 'var(--petrol)', fontStyle: 'italic', marginBottom: '1rem', fontSize: 'var(--text-body)' }}>
        {data.verdict}
      </p>

      <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '1rem' }}>
        {data.intro}
      </p>

      <ResultBlock icon={<Heart size={16} />} label="Amor" color="#ec4899" text={data.love} />
      <ResultBlock icon={<Sparkles size={16} />} label="Forças" color="#4ade80" text={data.strengths} />
      <ResultBlock icon={<AlertTriangle size={16} />} label="Desafios" color="#f59e0b" text={data.challenges} />
      <ResultBlock icon={<MessageCircle size={16} />} label="Comunicação" color="#60a5fa" text={data.communication} />

      <div style={{
        marginTop: '1.25rem',
        padding: '0.75rem 1rem',
        background: 'rgba(245,200,66,0.06)',
        border: '1px solid rgba(245,200,66,0.2)',
        borderRadius: 'var(--radius-md)',
      }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', margin: 0, lineHeight: 1.5 }}>
          ✦ Esta é apenas a camada solar. A Sinastria completa cruza todos os planetas, casas e aspectos dos dois mapas natais.
        </p>
      </div>
    </div>
  );
}

function ResultBlock({ icon, label, color, text }: { icon: React.ReactNode; label: string; color: string; text: string }) {
  return (
    <div style={{ marginBottom: '0.9rem' }}>
      <p style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        color,
        fontSize: 'var(--text-caption)',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        marginBottom: '0.3rem',
      }}>
        {icon} {label}
      </p>
      <p style={{ color: 'var(--text-secondary)', lineHeight: 1.65, margin: 0, fontSize: 'var(--text-body)' }}>
        {text}
      </p>
    </div>
  );
}

// ─── Contrast section: free vs full Sinastria ────────────────────
export function SynastryContrast() {
  return (
    <section style={{ padding: '3rem 1.5rem', maxWidth: '980px', margin: '0 auto' }}>
      <h2 className="font-display" style={{
        fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: '0.75rem',
      }}>
        A Sinastria completa vai infinitamente além
      </h2>
      <p style={{
        textAlign: 'center',
        color: 'var(--text-secondary)',
        marginBottom: '2rem',
        lineHeight: 1.65,
      }}>
        A análise por signo solar é só a primeira camada. A Sinastria de verdade cruza os dois mapas natais inteiros.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '1rem',
      }}>
        {/* Free sample */}
        <div className="card" style={{
          padding: '1.5rem',
          border: '1px solid var(--border-subtle)',
          background: 'rgba(255,255,255,0.02)',
          opacity: 0.85,
        }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, marginBottom: '0.5rem' }}>
            Análise por signo · Gratuita
          </p>
          <h3 style={{ fontSize: '1.05rem', color: 'var(--text-primary)', marginBottom: '0.75rem', fontWeight: 600 }}>
            Um panorama rápido
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: 'var(--text-body)' }}>
            <li>• Compatibilidade média dos signos</li>
            <li>• Dicas gerais sobre amor e comunicação</li>
            <li>• Visão simplificada de forças e tensões</li>
            <li>• Igual para todos que têm esses dois signos</li>
          </ul>
        </div>

        {/* Full Sinastria */}
        <div className="card" style={{
          padding: '1.5rem',
          border: '2px solid var(--petrol)',
          background: 'linear-gradient(180deg, rgba(245,200,66,0.08), rgba(0,0,0,0))',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--petrol)',
            color: '#000',
            fontSize: '0.7rem',
            padding: '2px 10px',
            borderRadius: '999px',
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}>
            ✦ SINASTRIA COMPLETA
          </div>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 700, marginBottom: '0.5rem' }}>
            Dois mapas natais inteiros
          </p>
          <h3 style={{ fontSize: '1.05rem', color: 'var(--text-primary)', marginBottom: '0.75rem', fontWeight: 600 }}>
            Profundidade real da relação
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: 'var(--text-secondary)', lineHeight: 1.7, fontSize: 'var(--text-body)' }}>
            <li>✦ Todos os planetas dos dois mapas</li>
            <li>✦ Casas, ângulos e eixos (ASC, DSC, MC, IC)</li>
            <li>✦ Aspectos cruzados (Vênus dele × Marte dela, Lua × Sol, etc.)</li>
            <li>✦ Propósito kármico e Nódulos Lunares</li>
            <li>✦ Mapa composto: a "terceira entidade" da relação</li>
            <li>✦ <strong style={{ color: 'var(--petrol)' }}>Compatibilidade do momento atual</strong>, trânsitos planetários ativando a relação agora</li>
            <li>✦ Único para você, cada análise é feita sob medida</li>
          </ul>
          <p style={{
            marginTop: '0.75rem',
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
            lineHeight: 1.5,
          }}>
            A análise do momento atual revela o que os astros estão ativando NA RELAÇÃO de vocês agora, fases de intensidade, de distância, de crescimento conjunto ou de teste.
          </p>
        </div>
      </div>
    </section>
  );
}
